import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

// ─── Types ────────────────────────────────────────────────────────────

export interface NotificationItem {
  id: string;
  message: string;
  is_read: boolean;
  is_deleted: boolean;
  event_type: string;
  createdAt: string;
  updatedAt?: string;
  createdtime?: string;
  user?: {
    username: string;
    id: string;
    _id?: string;
  };
}

/**
 * Params accepted by `getNotificationList`. All optional — the backend may
 * ignore unknown ones in early phases; the UI doesn't care because each
 * filter is null-safe at the URL builder level.
 */
export interface NotificationListParams {
  skip?: number;
  limit?: number;
  /** Read/unread filter — `undefined` = both. */
  is_read?: boolean;
  /**
   * Optional bucket name matching one of our three event groups:
   *   "upload" | "completed" | "failed"
   * Backend should match by substring on `event_type`. Anything else =
   * no event-type filter applied.
   */
  event_type?: "upload" | "completed" | "failed" | "all";
  /** ISO date — only notifications created on/after this moment. */
  date_from?: string;
  /** ISO date — only notifications created on/before this moment. */
  date_to?: string;
  /** Free-text search over message/username/event_type. */
  search?: string;
}

/** Channel-level on/off toggles. */
export interface ChannelToggle {
  bell: boolean;
  push: boolean;
  email: boolean;
}

/**
 * User notification preferences as stored on the backend. `event_overrides`
 * is keyed by NotificationEventType enum value, e.g. "DOCUMENT_UPLOADED".
 */
export interface NotificationPreferences {
  _id?: string;
  id?: string;
  user_id?: string;
  enabled: boolean;
  channel_defaults: ChannelToggle;
  event_overrides: Record<string, ChannelToggle>;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Push subscription row as stored on the backend. One row per (user, device).
 * `id` is the backend row id; `subscription_id` is the OneSignal device token.
 */
export interface PushSubscriber {
  _id?: string;
  id: string;
  user_id?: string;
  subscription_id: string;
  browser?: string;
  os?: string;
  user_agent?: string;
  location?: string;
  subscribed_at?: string;
  last_seen_at?: string;
  is_deleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Shape the API returns. Some fields are optional so older backends still work. */
export interface NotificationListResponse {
  success: boolean;
  code: number;
  data:
    | NotificationItem[]
    | {
        data: NotificationItem[];
        total_count?: number;
        unread_count?: number;
        counts?: { all: number; unread: number; read: number };
      };
}

/**
 * Normalised result the UI consumes.
 *   total_count    — matches the active filter (drives pagination)
 *   counts         — grand totals across ALL the user's notifications,
 *                    independent of the active filter, so the pill counts
 *                    (All/Unread/Read) stay accurate regardless of state.
 *   unread_count   — kept for the bell badge / legacy callers (mirror of counts.unread).
 */
export interface NotificationListResult {
  data: NotificationItem[];
  total_count: number;
  unread_count: number;
  counts: {
    all: number;
    unread: number;
    read: number;
  };
}

// ─── URL builder ──────────────────────────────────────────────────────

/**
 * Build the query string for `/notification-srv/all`. Only appends params
 * that are actually set (`undefined` → skipped) so the URL stays clean and
 * the backend doesn't see empty values to ignore.
 */
function buildListQuery(p: NotificationListParams): string {
  const parts: string[] = [];
  if (p.skip !== undefined) parts.push(`skip=${p.skip}`);
  if (p.limit !== undefined) parts.push(`limit=${p.limit}`);
  if (p.is_read !== undefined) parts.push(`is_read=${p.is_read}`);
  // event_bucket — UI alias used by the My Notifications tab. The backend
  // resolves it to one or more NotificationEventType enum values.
  if (p.event_type && p.event_type !== "all") parts.push(`event_bucket=${encodeURIComponent(p.event_type)}`);
  if (p.date_from) parts.push(`date_from=${encodeURIComponent(p.date_from)}`);
  if (p.date_to) parts.push(`date_to=${encodeURIComponent(p.date_to)}`);
  if (p.search) parts.push(`search=${encodeURIComponent(p.search)}`);
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

// ─── Service ──────────────────────────────────────────────────────────

class NotificationService {

  /**
   * Server-side filtered + paginated listing. Returns total / unread counts
   * separately so pagination + status pill counts don't lie when the
   * caller fetched only a single page.
   *
   * If the backend response is the legacy `data: NotificationItem[]` shape
   * (no total_count), we fall back to using the array length as the total
   * and recompute unread_count client-side. New backend shape
   * (`{ data, total_count, unread_count }`) is preferred.
   */
  async getNotificationList(params: NotificationListParams = {}): Promise<NotificationListResult> {
    try {
      const url = API_ENDPOINTS.NOTIFICATION.LIST + buildListQuery(params);
      const response = await apiClient.get<NotificationListResponse>(url);
      const body = response.data?.data;

      if (Array.isArray(body)) {
        // Legacy backend shape — only an array. Approximate the grand totals
        // from the array itself; counts will look wrong if pagination is on
        // but the bell badge still works.
        const unread = body.filter((n) => !n.is_read).length;
        return {
          data: body,
          total_count: body.length,
          unread_count: unread,
          counts: { all: body.length, unread, read: body.length - unread },
        };
      }

      if (body && typeof body === "object") {
        const items = Array.isArray(body.data) ? body.data : [];
        const total_count = typeof body.total_count === "number" ? body.total_count : items.length;
        const unread_count =
          typeof body.unread_count === "number"
            ? body.unread_count
            : items.filter((n) => !n.is_read).length;
        // counts block — new backend; if missing, fall back to whatever we
        // have so the UI still renders something sensible.
        const counts = body.counts ?? {
          all: total_count,
          unread: unread_count,
          read: Math.max(0, total_count - unread_count),
        };
        return { data: items, total_count, unread_count, counts };
      }

      return {
        data: [],
        total_count: 0,
        unread_count: 0,
        counts: { all: 0, unread: 0, read: 0 },
      };
    } catch (error) {
      console.error("Error fetching notification list:", error);
      return {
        data: [],
        total_count: 0,
        unread_count: 0,
        counts: { all: 0, unread: 0, read: 0 },
      };
    }
  }

  /**
   * Mark one or many notifications as read. Throws on failure so the
   * caller's catch block fires (the old `return []` silenced 404s,
   * which made the bell show "success" toasts for failed requests).
   */
  async postNotificationRead(data: { ids: string[] }): Promise<any> {
    const response = await apiClient.post<any>(
      API_ENDPOINTS.NOTIFICATION.MARK_AS_READ,
      data,
    );
    return response.data?.data ?? response.data ?? [];
  }

  /**
   * Mark every unread notification for the caller as read in one call.
   * No request body needed — the recipient is identified by the JWT.
   * Throws on failure — see `postNotificationRead` rationale.
   */
  async markAllAsRead(): Promise<any> {
    const response = await apiClient.post<any>(
      API_ENDPOINTS.NOTIFICATION.MARK_ALL_AS_READ,
    );
    return response.data?.data ?? response.data ?? [];
  }

  /** Fetch the caller's notification preferences (creates defaults on first call). */
  async getPreferences(): Promise<NotificationPreferences | null> {
    try {
      const res = await apiClient.get<any>(API_ENDPOINTS.NOTIFICATION.PREFERENCES);
      return res.data?.data ?? null;
    } catch (error) {
      console.error("Error fetching notification preferences:", error);
      return null;
    }
  }

  /** Patch the caller's notification preferences. */
  async updatePreferences(payload: Partial<NotificationPreferences>): Promise<NotificationPreferences | null> {
    try {
      const res = await apiClient.put<any>(API_ENDPOINTS.NOTIFICATION.PREFERENCES, payload);
      return res.data?.data ?? null;
    } catch (error) {
      console.error("Error updating notification preferences:", error);
      throw error;
    }
  }

  /** Register the current device's OneSignal subscription server-side. */
  async registerPushSubscription(payload: {
    subscription_id: string;
    browser?: string;
    os?: string;
    user_agent?: string;
    location?: string;
  }): Promise<PushSubscriber | null> {
    try {
      const res = await apiClient.post<any>(API_ENDPOINTS.NOTIFICATION.PUSH_SUBSCRIBE, payload);
      return res.data?.data ?? null;
    } catch (error) {
      console.error("Error registering push subscription:", error);
      throw error;
    }
  }

  /** List the caller's active push subscriptions across all devices. */
  async listPushSubscribers(): Promise<PushSubscriber[]> {
    try {
      const res = await apiClient.get<any>(API_ENDPOINTS.NOTIFICATION.PUSH_SUBSCRIBERS);
      return res.data?.data || [];
    } catch (error) {
      console.error("Error listing push subscribers:", error);
      return [];
    }
  }

  /** Soft-delete a specific push subscription (by row id, not OneSignal id). */
  async unregisterPushSubscription(rowId: string): Promise<any> {
    try {
      const res = await apiClient.delete<any>(API_ENDPOINTS.NOTIFICATION.PUSH_UNSUBSCRIBE(rowId));
      return res.data?.data ?? null;
    } catch (error) {
      console.error("Error unregistering push subscription:", error);
      throw error;
    }
  }

  /** Soft-delete one or more notifications belonging to the caller. Throws on failure. */
  async deleteNotifications(ids: string[]): Promise<any> {
    const response = await apiClient.post<any>(
      API_ENDPOINTS.NOTIFICATION.DELETE,
      { ids },
    );
    return response.data?.data ?? response.data ?? [];
  }
}

export const notificationService = new NotificationService();
export default notificationService;
